import { type FC, useMemo, useState } from "react";
import { CalloutIntent, DialogComponent, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import { createSelectionModel } from "@/app-utils/selection-model.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import type { InputTableQuerySpec } from "@/graphql.ts";
import { createUniqueKeyFromTableSpec } from "@/redux/modules/table-data.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE } from "@/settings/settings.ts";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";

interface SampleGridControlledDialogProps {
  onApply: (sampleIds: number[]) => void;
  isDisabled?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  selection: number[];
  sampleTypeId: number;
}

// 500 still loads almost instantly. Keeping this limit for now
// until we improved the UI with SEL10-1017
const SELECTION_LIMIT = 500;

const SampleGridControlledDialogRender: FC<SampleGridControlledDialogProps> = ({
  onApply,
  selection,
  onClose,
  sampleTypeId,
}) => {
  const [sampleIds, setSampleIds] = useState<number[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const sampleTableQuerySpec = useMemo(() => {
    return {
      id: Number(sampleTypeId),
      idType: DATATABLE_ID_TYPE.ENTITY,
      queryType: ENTITY_TYPE.SAMPLE_TYPE,
      resultType: ENTITY_TYPE.SAMPLE,
    } satisfies InputTableQuerySpec;
  }, [sampleTypeId]);

  return (
    <DialogComponent
      closeFn={onClose}
      mainButton={{
        label: "Apply",
        onPress: () => {
          onApply(sampleIds);
          onClose();
        },
        isDisabled: !!selectionError,
      }}
      actions={[
        <ColumnSelectionButton
          portalContext={createUniqueKeyFromTableSpec(sampleTableQuerySpec)}
          key="sample-selection-widget"
        />,
      ]}
      title="Select Samples"
      fullscreen
      contentStyle={{ padding: 0 }}
      errorCallout={selectionError ? { message: selectionError, intent: CalloutIntent.Warning } : undefined}
    >
      <div className="grid-modal-border" style={{ height: "100%" }}>
        <DataTableContainer
          key={`${createUniqueKeyFromTableSpec(sampleTableQuerySpec)}`}
          tableSpec={sampleTableQuerySpec}
          tableInitializationOptions={{
            sortExpression: "-created",
            initialSelectionModel: createSelectionModel(selection ?? []),
          }}
          onSelectionModelChanged={(selectionModel) => {
            if (selectionModel.selectedIds.length > SELECTION_LIMIT) {
              setSelectionError(
                `You cannot select more than ${SELECTION_LIMIT} ${pluralizeWord("sample", SELECTION_LIMIT)}`,
              );
            } else {
              setSelectionError(null);
              setSampleIds(selectionModel.selectedIds as number[]);
            }
          }}
        />
      </div>
    </DialogComponent>
  );
};

export const SampleGridControlledDialog: FC<SampleGridControlledDialogProps> = (props) => {
  const { overlayProps, state } = useOverlay({
    isOpen: props.isOpen,
    onOpenChange(isOpen) {
      if (!isOpen) props.onClose();
    },
  });

  return (
    <Modal state={state} {...overlayProps}>
      {/* Need to mount / unmount for resetting */}
      {props.isOpen && <SampleGridControlledDialogRender {...props} />}
    </Modal>
  );
};
